import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMembers = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/members`, {
        withCredentials: true
      });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tierColors = {
    essential: "bg-blue-100 text-blue-800",
    standard: "bg-purple-100 text-purple-800",
    plus: "bg-emerald-100 text-emerald-800"
  };

  const statusColors = {
    active: "bg-green-100 text-green-800",
    inactive: "bg-gray-100 text-gray-800",
    canceled: "bg-red-100 text-red-800"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2" data-testid="members-title">
                Members
              </h1>
              <p className="text-muted-foreground">{members.length} total members</p>
            </div>
          </div>

          {members.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No members yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="members-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 font-semibold">Name</th>
                    <th className="text-left py-4 px-4 font-semibold">Email</th>
                    <th className="text-left py-4 px-4 font-semibold">Location</th>
                    <th className="text-left py-4 px-4 font-semibold">Tier</th>
                    <th className="text-left py-4 px-4 font-semibold">Status</th>
                    <th className="text-right py-4 px-4 font-semibold">Total Advanced</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr 
                      key={member.user_id}
                      data-testid={`member-row-${member.user_id}`}
                      className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="font-medium">{member.name}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-muted-foreground">{member.email}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm">
                          {member.city && member.state ? `${member.city}, ${member.state}` : '-'}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {member.membership_tier ? (
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${tierColors[member.membership_tier] || 'bg-gray-100'}`}>
                            {member.membership_tier}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">None</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColors[member.membership_status] || 'bg-gray-100'}`}>
                          {member.membership_status || 'inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="font-semibold">${member.total_advanced?.toFixed(2) || '0.00'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMembers;
